import { useCallback, useEffect, useMemo, useState } from 'react';
import { Clock, Home, Pin, Plus, Search } from 'lucide-react';
import { navigateTo } from '@/lib/navigate';
import { objectTypeIcon, type ObjectIcon } from '@/lib/objectIcons';
import { settingsStore } from '@/lib/settingsStore';
import { Input } from '@/components/ui/input';
import {
  useEnterpriseSections,
  sectionObjectName,
  flattenSections,
  type EnterpriseSection,
} from '@/features/enterprise/shell/useEnterpriseSections';
import { WidgetShell, EmptyState } from './builtinWidgets';
import { loadHubEntries, hubRefKey, type HubBadge, type HubEntry, type HubRef } from './quickAccessHubData';
import type { WidgetContext, WidgetProps } from './registry';

// ============================================================================
// lib/widgets/quickAccessHub.tsx — ВИДЖЕТ «Быстрый доступ (карточки разделов)».
// Рабочий стол выводит разделы и быстрые действия КАРТОЧКАМИ с навигацией
// (не только через меню «Пуск»). Разделы — из ЕДИНОГО источника видимости
// (useEnterpriseSections — тот же, что у StartMenu/Drawer: права и объекты
// резолвит сервер). Навигация — существующие сервисы (ctx.onSectionOpen /
// ctx.onObjectOpen; в Конфигураторе — маршруты /designer/section|object).
//
// ЗОНЫ: «Закреплённые» (выбор + порядок пользователя), «Недавние» (последние
// открытые через хаб), «Быстрые действия» (создание документов/заявок),
// «Разделы» (все доступные). Закрепление/недавние — user_settings (SSoT).
// ============================================================================

type QaRef =
  | { kind: 'section'; id: string }
  | { kind: 'object'; objectType: string; objectName: string };

interface HubCard {
  key: string;
  ref: QaRef;
  label: string;
  hint: string;
  Icon: ObjectIcon;
  quick?: boolean;
}

const PINNED_KEY = 'quickAccessHub.pinned';
const RECENT_KEY = 'quickAccessHub.recent';
const RECENT_MAX = 5;
const EMPTY_REFS: QaRef[] = [];

function refKey(ref: QaRef): string {
  return ref.kind === 'section' ? `s:${ref.id}` : `o:${ref.objectType}.${ref.objectName}`;
}

function parseRefs(raw: unknown): QaRef[] {
  if (!Array.isArray(raw)) return [];
  const out: QaRef[] = [];
  for (const item of raw) {
    const r = item as QaRef;
    if (r?.kind === 'section' && typeof r.id === 'string' && r.id) out.push({ kind: 'section', id: r.id });
    else if (r?.kind === 'object' && typeof r.objectType === 'string' && typeof r.objectName === 'string' && r.objectName) {
      out.push({ kind: 'object', objectType: r.objectType, objectName: r.objectName });
    }
  }
  return out;
}

/** Единое чтение/запись JSON-настройки пользователя (settingsStore, SSoT). */
function useStoreRefs(key: string): [QaRef[], (v: QaRef[]) => void] {
  const [value, setValue] = useState<QaRef[]>(() => parseRefs(settingsStore.getJSON<unknown>(key, EMPTY_REFS)));
  useEffect(() => {
    const read = () => setValue(parseRefs(settingsStore.getJSON<unknown>(key, EMPTY_REFS)));
    read();
    return settingsStore.subscribe(read);
  }, [key]);
  const write = useCallback((next: QaRef[]) => {
    setValue(next);
    settingsStore.setJSON(key, next);
  }, [key]);
  return [value, write];
}

/** Резолв ссылки в карточку по ТЕКУЩИМ доступным разделам (права — сервер). */
function resolveRef(ref: QaRef, sections: EnterpriseSection[], synonyms: Record<string, string>): HubCard | null {
  if (ref.kind === 'section') {
    const s = sections.find((x) => x.id === ref.id);
    if (!s) return null;
    return { key: refKey(ref), ref, label: s.name, hint: 'Раздел', Icon: Home };
  }
  for (const s of sections) {
    const o = s.objects.find((x) => x.objectType === ref.objectType && x.objectName === ref.objectName);
    if (o) {
      return { key: refKey(ref), ref, label: sectionObjectName(o, synonyms), hint: s.name, Icon: objectTypeIcon(o.objectType) };
    }
  }
  return null;
}

function sectionCards(sections: EnterpriseSection[]): HubCard[] {
  return sections.map((s) => ({
    key: `s:${s.id}`,
    ref: { kind: 'section', id: s.id },
    label: s.name,
    hint: 'Раздел',
    Icon: Home,
  }));
}

/** Быстрые действия — создаваемые типы (документ/заявка) из доступных разделов. */
function quickActionCards(sections: EnterpriseSection[], synonyms: Record<string, string>): HubCard[] {
  const out: HubCard[] = [];
  const seen = new Set<string>();
  for (const s of sections) {
    for (const o of s.objects) {
      if (o.objectType !== 'document' && o.objectType !== 'order') continue;
      const key = `o:${o.objectType}.${o.objectName}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        key,
        ref: { kind: 'object', objectType: o.objectType, objectName: o.objectName },
        label: sectionObjectName(o, synonyms),
        hint: 'Новая запись',
        Icon: Plus,
        quick: true,
      });
    }
  }
  return out.slice(0, 4);
}

// --- Карточка -----------------------------------------------------------------

const GRID = 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2';

function HubCardView({
  card, pinned, onOpen, onTogglePin, onDragStart, onDrop,
}: {
  card: HubCard;
  pinned: boolean;
  onOpen: () => void;
  onTogglePin: () => void;
  onDragStart?: () => void;
  onDrop?: () => void;
}) {
  const { Icon } = card;
  return (
    <button
      type="button"
      draggable={!!onDragStart}
      onDragStart={onDragStart}
      onDragOver={onDrop ? (e) => e.preventDefault() : undefined}
      onDrop={onDrop}
      onClick={onOpen}
      className="group relative flex items-center gap-2.5 rounded-xl border bg-card px-3 py-2.5 text-left transition-all hover:border-primary/40 hover:bg-accent/40 hover:shadow-sm active:scale-[0.98]"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon className="h-4 w-4" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block truncate text-sm font-medium">{card.label}</span>
        <span className="block truncate text-[11px] text-muted-foreground">{card.hint}</span>
      </span>
      {!card.quick && (
        <span
          role="button"
          tabIndex={-1}
          title={pinned ? 'Убрать из закреплённых' : 'Закрепить'}
          onClick={(e) => { e.stopPropagation(); onTogglePin(); }}
          className={`shrink-0 rounded-md p-1 transition-colors ${pinned ? 'text-primary' : 'text-muted-foreground/40 opacity-0 group-hover:opacity-100'}`}
        >
          <Pin className="h-3.5 w-3.5" fill={pinned ? 'currentColor' : 'none'} />
        </span>
      )}
    </button>
  );
}

// --- Виджет -------------------------------------------------------------------

export function QuickAccessHubWidget(props: WidgetProps, ctx: WidgetContext) {
  const { sections, synonyms, error, reload } = useEnterpriseSections();
  const [pinned, setPinned] = useStoreRefs(PINNED_KEY);
  const [recent, setRecent] = useStoreRefs(RECENT_KEY);
  const [query, setQuery] = useState('');
  const [dragKey, setDragKey] = useState<string | null>(null);

  const showSearch = props.search === true;
  const source = String(props.source || 'auto');
  const cardCount = Math.max(1, Number(props.cardCount) || 12);

  // Дерево разделов → плоский список (карточки всех разделов/групп, включая вложенные)
  const list = useMemo(() => flattenSections(sections || []), [sections]);

  const pinnedCards = useMemo(
    () => pinned.map((r) => resolveRef(r, list, synonyms)).filter((c): c is HubCard => c !== null),
    [pinned, list, synonyms],
  );
  const recentCards = useMemo(() => {
    const pinnedKeys = new Set(pinnedCards.map((c) => c.key));
    return recent
      .map((r) => resolveRef(r, list, synonyms))
      .filter((c): c is HubCard => c !== null && !pinnedKeys.has(c.key));
  }, [recent, list, synonyms, pinnedCards]);
  const quickCards = useMemo(() => quickActionCards(list, synonyms), [list, synonyms]);

  const openCard = useCallback((ref: QaRef) => {
    setRecent([ref, ...recent.filter((r) => refKey(r) !== refKey(ref))].slice(0, RECENT_MAX));
    if (ref.kind === 'section') {
      if (ctx.onSectionOpen) ctx.onSectionOpen(ref.id);
      else navigateTo(`/designer/section/${encodeURIComponent(ref.id)}`);
    } else if (ctx.onObjectOpen) {
      ctx.onObjectOpen(ref.objectType, ref.objectName);
    } else {
      navigateTo(`/designer/object/${encodeURIComponent(ref.objectType)}/${encodeURIComponent(ref.objectName)}`);
    }
  }, [ctx, recent, setRecent]);

  const togglePin = useCallback((ref: QaRef) => {
    const key = refKey(ref);
    const has = pinned.some((r) => refKey(r) === key);
    setPinned(has ? pinned.filter((r) => refKey(r) !== key) : [...pinned, ref]);
  }, [pinned, setPinned]);

  const dropPinned = (targetKey: string) => {
    if (!dragKey || dragKey === targetKey) { setDragKey(null); return; }
    const from = pinned.findIndex((r) => refKey(r) === dragKey);
    const to = pinned.findIndex((r) => refKey(r) === targetKey);
    if (from >= 0 && to >= 0) {
      const next = pinned.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      setPinned(next);
    }
    setDragKey(null);
  };

  const q = query.trim().toLowerCase();
  const matches = (c: HubCard) => !q || c.label.toLowerCase().includes(q);
  const used = pinnedCards.length + recentCards.length + (source === 'pinned' ? 0 : quickCards.length);
  const autoRemaining = Math.max(0, cardCount - used);
  const sectionZone = source === 'pinned'
    ? []
    : sectionCards(list).filter((c) => !pinnedCards.some((p) => p.key === c.key) && !recentCards.some((r) => r.key === c.key));
  const sectionsShown = (source === 'all' ? sectionZone : sectionZone.slice(0, autoRemaining)).filter(matches);

  const zones: Array<{ title: string; icon: React.ReactNode; items: HubCard[]; pinnedZone?: boolean }> = [];
  if (pinnedCards.filter(matches).length > 0) {
    zones.push({ title: 'Закреплённые', icon: <Pin className="h-3.5 w-3.5" />, items: pinnedCards.filter(matches), pinnedZone: true });
  }
  if (source !== 'pinned' && recentCards.filter(matches).length > 0) {
    zones.push({ title: 'Недавние', icon: <Clock className="h-3.5 w-3.5" />, items: recentCards.filter(matches) });
  }
  if (source !== 'pinned' && quickCards.filter(matches).length > 0) {
    zones.push({ title: 'Быстрые действия', icon: <Plus className="h-3.5 w-3.5" />, items: quickCards.filter(matches) });
  }
  if (sectionsShown.length > 0) {
    zones.push({ title: 'Разделы', icon: <Home className="h-3.5 w-3.5" />, items: sectionsShown });
  }

  return (
    <WidgetShell
      title={props.title || 'Быстрый доступ'}
      showTitle={props.showTitle}
      showBorder={props.showBorder}
      icon={<Home className="h-4 w-4" />}
    >
      {showSearch && (
        <div className="relative shrink-0 pb-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Поиск разделов…"
            className="h-8 pl-7 text-xs"
          />
        </div>
      )}

      {sections === null && !error && <EmptyState text="Загрузка разделов…" />}
      {error && (
        <button
          type="button"
          onClick={reload}
          className="rounded-md px-2 py-1.5 text-left text-xs text-destructive hover:bg-destructive/10"
        >
          Разделы не загрузились. Нажмите для повторной попытки.
        </button>
      )}
      {sections !== null && zones.length === 0 && <EmptyState text={q ? 'Ничего не найдено' : 'Нет доступных разделов'} />}

      <div className="flex flex-col gap-3">
        {zones.map((zone) => (
          <div key={zone.title}>
            <div className="mb-1 flex items-center gap-1.5 px-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {zone.icon}
              <span>{zone.title}</span>
            </div>
            <div className={GRID}>
              {zone.items.map((card) => (
                <HubCardView
                  key={card.key}
                  card={card}
                  pinned={pinned.some((r) => refKey(r) === card.key)}
                  onOpen={() => openCard(card.ref)}
                  onTogglePin={() => togglePin(card.ref)}
                  onDragStart={zone.pinnedZone ? () => setDragKey(card.key) : undefined}
                  onDrop={zone.pinnedZone ? () => dropPinned(card.key) : undefined}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </WidgetShell>
  );
}
